import { Field, Form } from "react-final-form";
import React, { useContext, useState } from "react";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import GqlClientContext from "src/lib/gqlClientContext";
import Grid from "@material-ui/core/Grid";
import LocaleLink from "src/components/Navigation/LocaleLink";
import MainLayout from "src/components/Layout/MainLayout";
import SeoHead from "src/components/Seo/SeoHead";
import TextField from "src/lib/forms/TextField";
import Typography from "@material-ui/core/Typography";
import { contact } from "src/graphql/queries";
import { makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useTranslation from "next-translate/useTranslation";

const contactWidth = 600;
const contactSendButtonWidth = 120;

const useStyles = makeStyles((theme) => ({
  partnerpage: {},
  header: {
    height: "75vh",
    paddingTop: "15vh",
    color: theme.palette.primary.main,
    textAlign: "center",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundColor: "#000",
    backgroundImage: "url('/images/Header-Partnerseite-Desktop.jpg')",
    [theme.breakpoints.down("xs")]: {
      paddingTop: "8vh",
      backgroundImage: "url('/images/Header-Partnerseite-Mobile.jpg')",
    },
  },
  headerContent: {
    maxWidth: 500,
    display: "inline-block",
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.globalMarginMobile,
      paddingRight: theme.globalMarginMobile,
    },
  },
  title: {
    ...theme.headline2,
  },
  intro: {
    fontFamily: theme.highlightFontFamily,
    fontSize: 36,
    fontWeight: 900,
    marginTop: 0,
    "& span": {
      display: "inline-block",
      whiteSpace: "pre-wrap",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: 400,
    },
  },
  headerCTA: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.main,
    "& :hover": {
      color: theme.palette.primary.main,
    },
  },
  grid: {
    textAlign: "center",
  },
  description: {
    maxWidth: 720,
    margin: "auto",
  },
  descriptionHeadline: {
    fontSize: "large",
    paddingTop: theme.spacing(3),
    color: theme.hightlightColorTertiary,
  },
  descriptionIntro: {
    fontSize: "large",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(7),
  },
  contact: {
    textAlign: "center",
    maxWidth: contactWidth,
    margin: "auto",
    marginTop: theme.spacing(5),
  },
  contactIntro: {
    fontSize: "large",
  },
  sitePadding: {
    paddingRight: theme.globalMarginMobile,
    paddingLeft: theme.globalMarginMobile,
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.globalMarginDesktop,
      paddingRight: theme.globalMarginDesktop,
    },
  },
  form: {
    textAlign: "left",
    "& input": {
      backgroundColor: theme.palette.background.secondary,
      color: theme.palette.secondary.main,
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.secondary.main,
      zIndex: 1,
      pointerEvents: "none",
      paddingLeft: 15,
      paddingTop: 5,
    },
    "& .MuiTextField-root": {
      marginBottom: theme.spacing(2),
      "& input": {
        height: 30,
        paddingLeft: 15,
      },
    },
  },
  checkboxIntro: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    fontSize: 24,
  },
  checkbox: {
    marginBottom: theme.spacing(2),
    "& label": {
      fontSize: "large",
    },
    "& input": {
      zoom: "1.5",
    },
  },
  privacy: {
    fontSize: "small",
    display: "inline-block",
    maxWidth: contactWidth - contactSendButtonWidth - theme.globalMarginDesktop * 2,
    "& a": {
      color: "inherit",
      textDecoration: "underline",
    },
    width: "100%",
  },
  buttonSendContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.up("sm")]: {
      display: "flex",
    },
  },
  buttonSend: {
    width: contactSendButtonWidth,
    backgroundColor: "#05B709",
  },
  buttonSendWrap: {
    textAlign: "center",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(1),
    },
  },
  success: {
    marginBottom: 80,
    marginTop: 80,
    fontSize: 36,
    fontFamily: theme.highlightFontFamily,
    color: "#05B709",
    lineHeight: "1.2",
  },
}));

const useStylesOffer = makeStyles((theme) => ({
  offer: {
    maxWidth: 233,
    margin: "auto",
    height: "100%",
    position: "relative",
  },
  icon: {
    height: 100,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
  },
  title: {
    fontSize: 22,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  text: {
    marginBottom: theme.spacing(16),
    fontSize: "large",
  },
  button: {
    position: "absolute",
    bottom: theme.spacing(8),
    width: "100%",
  },
}));

const scrollToFrom = () => {
  const element = document.getElementById("contactform");
  setTimeout(() => {
    window.scrollTo({
      behavior: "smooth",
      top: element.offsetTop,
    });
  }, 100);
};

const Offer = (props) => {
  const { title1, title2, title3, text, img, color } = props;
  const { t } = useTranslation("partner");
  const classes = useStylesOffer();

  return (
    <div className={classes.offer}>
      <div className={classes.icon} style={{ backgroundImage: `url('${img}')` }} />
      <div className={classes.title} style={{ color }}>
        <span>{title1}</span>
        <span style={{ fontWeight: 600 }}>{title2}</span>
        <span>{title3}</span>
      </div>
      <div className={classes.text}>{text}</div>
      <div className={classes.button}>
        <Button
          style={{ backgroundColor: color, color: "#fff" }}
          onClick={() => {
            scrollToFrom();
          }}
        >
          {t("partnerCTAButton")}
        </Button>
      </div>
    </div>
  );
};

const required = (value) => (value ? undefined : "Required");

const Partner = () => {
  const { t } = useTranslation("partner");
  const classes = useStyles();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formInProgress, setFormInProgress] = useState(false);
  const isDesktopView = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const gqlFetcher = useContext(GqlClientContext);

  const onSubmit = async (values) => {
    setFormInProgress(true);
    const message = JSON.stringify(values);
    await gqlFetcher([contact, JSON.stringify({ message })]);
    setFormInProgress(false);
    setFormSubmitted(true);
  };

  return (
    <MainLayout variant="partner">
      <SeoHead title={t("common:footerPartner")} />
      <div className={classes.partnerpage} data-test="page-partner">
        <div className={classes.header}>
          <div className={classes.headerContent}>
            <p className={classes.title}>
              <span>{t("partnerTitle")}</span>
            </p>
            <p className={classes.intro}>
              <span>{t("partnerIntro1")}</span>
              <span>{t("partnerIntro2")}</span>
              <span>{t("partnerIntro3")}</span>
            </p>
            <Button
              className={classes.headerCTA}
              onClick={() => {
                scrollToFrom();
              }}
            >
              {t("partnerCTAButton")}
            </Button>
          </div>
        </div>

        <Grid container className={`${classes.grid} ${classes.sitePadding}`}>
          <Grid item xs={12} className={classes.description}>
            <Typography variant="body1" className={classes.descriptionHeadline}>
              {t("partnerDescriptionHeadline1")}
            </Typography>
            <Typography variant="h3">{t("partnerDescriptionHeadline2")}</Typography>
            <Typography variant="body1" className={classes.descriptionIntro}>
              {t("partnerDescriptionIntro")}
            </Typography>
          </Grid>
        </Grid>
        <Grid container className={`${classes.grid} ${classes.sitePadding}`}>
          <Grid item xs={12} sm={4}>
            <Offer
              color="#9C00A2"
              title2={t("partnerOffer1Title1")}
              title3={t("partnerOffer1Title2")}
              text={t("partnerOffer1Text")}
              img="/images/partner_sales.svg"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Offer
              color="#05B709"
              title1={t("partnerOffer2Title1")}
              title2={t("partnerOffer2Title2")}
              title3={t("partnerOffer2Title3")}
              text={t("partnerOffer2Text")}
              img="/images/partner_traffic.svg"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Offer
              color="#0096FF"
              title2={t("partnerOffer3Title1")}
              title3={t("partnerOffer3Title2")}
              text={t("partnerOffer3Text")}
              img="/images/partner_content.svg"
            />
          </Grid>
        </Grid>

        <div id="contactform" className={`${classes.contact} ${classes.sitePadding}`}>
          <Typography variant="h3">{t("partnerContactHeadline")}</Typography>

          <p className={classes.contactIntro}>
            <span>{t("partnerContactIntro1")}</span>
            <span style={{ fontWeight: 900 }}>{t("partnerContactIntro2")}</span>
            <span>{t("partnerContactIntro3")}</span>
          </p>

          {formInProgress && (
            <div>
              <CircularProgress color="secondary" />
            </div>
          )}

          {formSubmitted && <div className={classes.success}>{t("partnerContactSuccess")}</div>}

          {!formSubmitted && !formInProgress && (
            <Form
              onSubmit={(values) => onSubmit(values)}
              render={({ handleSubmit, form, submitting }) => (
                <form onSubmit={handleSubmit} className={classes.form}>
                  <Field
                    name="name"
                    validate={required}
                    fullWidth
                    label={t("partnerContactName")}
                    type="text"
                    component={TextField}
                  />

                  <Field
                    name="email"
                    validate={required}
                    fullWidth
                    label={t("partnerContactEmail")}
                    type="text"
                    component={TextField}
                  />

                  <Field
                    name="phone"
                    validate={required}
                    fullWidth
                    label={t("partnerContactPhone")}
                    type="text"
                    component={TextField}
                  />

                  <Field
                    name="company"
                    validate={required}
                    fullWidth
                    label={t("partnerContactCompany")}
                    type="text"
                    component={TextField}
                  />

                  <Typography variant="h6" className={classes.checkboxIntro}>
                    {t("partnerContactFormats")}
                  </Typography>

                  <div className={classes.checkbox}>
                    <label>
                      <Field name="checkboxSales" component="input" type="checkbox" value="yes" />{" "}
                      {t("partnerContactCheckboxSales")}
                    </label>
                  </div>

                  <div className={classes.checkbox}>
                    <label>
                      <Field name="checkboxTraffic" component="input" type="checkbox" value="yes" />{" "}
                      {t("partnerContactCheckboxTraffic")}
                    </label>
                  </div>

                  <div className={classes.checkbox}>
                    <label>
                      <Field name="checkboxContent" component="input" type="checkbox" value="yes" />{" "}
                      {t("partnerContactCheckboxContent")}
                    </label>
                  </div>

                  <div className={classes.buttonSendContainer}>
                    {isDesktopView && (
                      <div className={classes.privacy}>
                        <span>{t("partnerContactPrivacy1")}</span>
                        <span>
                          <LocaleLink notStyled href="/privacy" as="/privacy" target="new">
                            {t("partnerContactPrivacy2")}
                          </LocaleLink>
                        </span>
                        <span>{t("partnerContactPrivacy3")}</span>
                      </div>
                    )}

                    <div className={classes.buttonSendWrap}>
                      <Button type="submit" color="secondary" className={classes.buttonSend} disabled={submitting}>
                        {t("partnerContactSubmit")}
                      </Button>
                    </div>

                    {!isDesktopView && (
                      <div className={classes.privacy}>
                        <span>{t("partnerContactPrivacy1")}</span>
                        <span>
                          <LocaleLink notStyled href="/privacy" as="/privacy" target="new">
                            {t("partnerContactPrivacy2")}
                          </LocaleLink>
                        </span>
                        <span>{t("partnerContactPrivacy3")}</span>
                      </div>
                    )}
                  </div>
                </form>
              )}
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Partner;
